<template>
    <main class="trip">
        <router-link class="trip__link trip__link_back link link_red"
            v-if="isReportTotalLinkVisible"
            :to="{ name: 'TripTotal'}"
        >
            Вернуться к общим данным рейсов
        </router-link>
        <form class="trip__settings" @submit.prevent>
            <h2 class="sr-only">Настройки отчета "Рейсы"</h2>
            <daterange-picker class="trip__daterange"
                v-model="daterangeModel"
                name="report-daterange"
            >
                Период отчета
            </daterange-picker>
            <fieldset class="trip__radio-group trip__radio-group_modes">
                <legend class="trip__legend sr-only">Режим отчета</legend>
                <radio class="trip__radio trip__radio_mode"
                    v-for="mode in modes"
                    :key="mode.id"
                    :val="mode.id"
                    name="mode"
                    v-model="modeModel"
                >
                    {{mode.title}}
                </radio>
            </fieldset>
            <daterange-picker class="trip__daterange"
                v-if="modeId === 'compareOtherPeriod'"
                v-model="compareDaterangeModel"
                name="compareOtherPeriod"
            >
                Период сравнения:
            </daterange-picker>
            <trip-settings class="trip-total__settings"  v-if="isShowRestaurantsFilter" />
        </form>
        <spinner class="trip__spinner" v-if="status === 'loading'" />
        <error-message class="trip__error-message" v-else-if="status === 'error'">
            Не удалось загрузить тайминг.
        </error-message>
        <router-view
            class="trip__data"
            :status="status"
            :reportDaterange="reportDaterange"
            :compareDaterange="compareDaterange"
            :modes="modes"
            :modeId="modeId"
            :reportTrip="reportTrip"
            :compareTrip="compareTrip"
            :isReportTotalLinkVisible="isReportTotalLinkVisible"
            :isShowRestaurantsFilter="isShowRestaurantsFilter"
            :userRestaurants="userRestaurants"
        />
    </main>
</template>

<script>
    import { mapState, mapGetters, mapMutations, mapActions } from "vuex";
    import { daterangeKey, isDaterangeSet } from "@/helpers/daterange";
    import DaterangePicker from "@/components/DaterangePicker";
    import TripSettings from "./trip/common/TripSettings";

    export default {
        name: "Trip",
        components: {
            TripSettings,
            DaterangePicker
        },
        data() {
            return {
                status: "loading"
            }
        },
        computed: {
            ...mapState({
                reportDaterange: state => state.report.daterange,
                reportRestaurants: state => state.report.restaurants,
                compareDaterange: state => state.trip.compareDaterange,
                modeId: state => state.trip.modeId,
                data: state => state.trip.data,
                isReportTotalLinkVisible: state => state.ui.isReportTotalLinkVisible,
                isShowRestaurantsFilter: state => state.ui.isShowRestaurantsFilter,
            }),
            ...mapGetters([
                "userRestaurants",
                "reportRestaurantsKey",
                "reportKey",
            ]),
            modes() {
                return [
                    { id: "base", title: "Базовые данные" },
                    { id: "compareOtherPeriod", title: "Сравнение с другим периодом" }
                ];
            },
            daterangeModel: {
                get() {
                    return this.reportDaterange;
                },
                set(daterange) {
                    this.setReportDaterange(daterange);
                }
            },
            compareDaterangeModel: {
                get() {
                    return this.compareDaterange;
                },
                set(daterange) {
                    this.setTripCompareDaterange(daterange);
                }
            },
            modeModel: {
                get() {
                    return this.modeId;
                },
                set(modeId) {
                    this.setTripMode(modeId);
                }
            },
            requiredDateranges() {
                let dateranges = [this.reportDaterange];

                if (this.modeId === "compareOtherPeriod" && isDaterangeSet(this.compareDaterange)) {
                    dateranges.push(this.compareDaterange);
                }

                return dateranges;
            },
            requiredTrips() {
                return this.requiredDateranges?.map(daterange => this.data[`${daterangeKey(daterange)}-${this.reportRestaurantsKey}`]);
            },
            reportTrip() {
                return this.data[this.reportKey];
            },
            compareTrip() {
                if (isDaterangeSet(this.compareDaterange)) {
                    return this.data[`${daterangeKey(this.compareDaterange)}-${this.reportRestaurantsKey}`];
                }
            }
        },
        methods: {
            ...mapMutations([
                "setReportDaterange",
                "setTripCompareDaterange",
                "setTripMode",
            ]),
            ...mapActions([
                "requestTrips"
            ]),
            showReport() {
                this.status = "success";
            },
            loadTrips() {
                this.status = "loading";

                let daterangesWithoutData = this.requiredDateranges.filter(
                    daterange => !Boolean(this.data[`${daterangeKey(daterange)}-${this.reportRestaurantsKey}`])
                );

                this.requestTrips({
                    dateranges: daterangesWithoutData,
                    restaurants: this.reportRestaurants,
                    restaurantsKey: this.reportRestaurantsKey,
                }).then(() => {
                    this.showReport();
                }).catch(() => {
                    this.status = "error";
                });
            }
        },
        created() {
            if (this.userRestaurants?.ids?.length === 1) {
                this.$router.push({
                    name: "TripRestaurant",
                    params:{
                        id: this.userRestaurants?.ids[0]
                    }
                });
            } else {
                if (this.reportRestaurants.length === 0) {
                    this.status = "no-restaurants";
                } else if (this.requiredTrips?.every(Boolean)) {
                    this.showReport();
                } else {
                    this.loadTrips();
                }
            }
        },
        watch: {
            requiredTrips(requiredTrips) {
                if (this.reportRestaurants.length === 0) {
                    this.status = "no-restaurants";
                } else if (!requiredTrips.every(Boolean)) {
                    this.loadTrips();
                } else {
                    this.showReport();
                }
            }
        }
    }
</script>

<style lang="scss">
    .trip__link {
        &_back {
            display: inline-block;
            margin-bottom: 10px;

            @include desktop {
                margin-bottom: 15px;
            }
        }
    }
    .trip__daterange {
        margin-bottom: 10px;

        @include desktop {
            margin-bottom: 15px;
        }
    }
    .trip__radio-group {
        display: flex;
        flex-flow: column nowrap;
        justify-content: flex-start;
        align-items: flex-start;
        margin-bottom: 10px;

        @include desktop {
            flex-direction: row;
            margin-bottom: 15px;
        }
    }
    .trip__radio {
        & + & {
            margin-top: 5px;

            @include desktop {
                margin: 0 0 0 20px;
            }
        }
    }
    .trip__legend {
        margin-bottom: 5px;

        @include desktop {
            margin-bottom: 10px;
        }
    }
    .trip__figcaption {
        margin: 0 0 15px 10px;
    }
    .trip__select {
        min-width: 240px;
        padding: 4px 8px;
        margin-bottom: 15px;
        border-color: $gray-line;
        border-radius: 4px;

        &:focus {
            outline: none;
        }
    }
</style>
